import { useRef, useState } from 'react'
import Avatar from './Avatar.jsx'
import ViewSwitcher from './ViewSwitcher.jsx'
import { Toolbar as KitToolbar, ToolbarSpacer, FilterBar, Popover, MenuItem, Chip, Button, Field, useOutsideClose } from '../ds/index.js'
import { CaretDown } from '@phosphor-icons/react'

// Inline label editor row used inside the label filter popover.
function LabelEditRow({ label, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [nameVal, setNameVal] = useState(label.name)
  const [colorVal, setColorVal] = useState(label.color)

  const submit = () => {
    const trimmed = nameVal.trim()
    if (trimmed && trimmed !== label.name) onUpdate(label.id, { name: trimmed })
    setEditing(false)
  }

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 9px' }}>
        <input
          value={nameVal}
          onChange={(e) => setNameVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setEditing(false); setNameVal(label.name) } }}
          autoFocus
          onClick={(e) => e.stopPropagation()}
          className="cn-input"
          style={{ flex: 1, fontSize: 12, padding: '5px 7px' }}
        />
        <input
          type="color"
          value={colorVal}
          onChange={(e) => { setColorVal(e.target.value); onUpdate(label.id, { color: e.target.value }) }}
          onClick={(e) => e.stopPropagation()}
          style={{ width: 24, height: 24, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
        />
        <button onClick={(e) => { e.stopPropagation(); submit() }} style={{ background: 'none', border: 'none', color: 'var(--color-gold)', fontSize: 11, cursor: 'pointer', padding: '2px 4px', fontWeight: 700 }}>✓</button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      <span style={{ flex: 1 }} />
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true) }}
        title="Edit label"
        style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 11, padding: '2px 5px', lineHeight: 1 }}
      >
        ✎
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(label.id) }}
        title="Delete label"
        style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 12, padding: '2px 5px', lineHeight: 1 }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-danger)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-muted)')}
      >
        ✕
      </button>
    </div>
  )
}

// Mock Slack inbox for the in-app capture demo (the production path is the
// Slack slash command Cloud Function).
const SLACK_MESSAGES = [
  { id: 's1', channel: '#mobile-bugs', author: 'Sam Chen', time: '9:24 AM', text: 'Login button unresponsive on iOS 17' },
  { id: 's2', channel: '#website', author: 'Maya Patel', time: '8:51 AM', text: 'Footer links return 404 after the latest deploy' },
]

export default function Toolbar({
  view,
  onSetView,
  labels,
  users,
  labelFilter,
  assigneeFilter,
  onSetLabelFilter,
  onSetAssigneeFilter,
  phaseFilter,
  onSetPhaseFilter,
  incompleteOnly,
  onSetIncompleteOnly,
  dueSoonOnly,
  onSetDueSoonOnly,
  onClearFilters,
  onCreateFromSlack,
  onCreateLabel,
  onUpdateLabel,
  onDeleteLabel,
  savedViews,
  onApplySavedView,
  onSaveCurrentView,
  onDeleteSavedView,
}) {
  const [open, setOpen] = useState(null) // 'label' | 'assignee' | 'slack' | 'views' | null
  const [newLabelMode, setNewLabelMode] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  const [newLabelColor, setNewLabelColor] = useState('#7A7568')
  const [newViewMode, setNewViewMode] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const toggle = (m) => setOpen((cur) => (cur === m ? null : m))
  // Saved-views panel stays mounted (not Popover-backed, see render below), so it
  // needs its own outside-close wiring.
  const viewsRef = useRef(null)
  useOutsideClose(viewsRef, open === 'views', () => { setOpen(null); setNewViewMode(false); setNewViewName('') })

  const labelFilterLabel = labelFilter ? labels.find((l) => l.id === labelFilter)?.name : 'All'
  const assigneeFilterLabel = assigneeFilter
    ? (users.find((u) => u.uid === assigneeFilter)?.name || '').split(' ')[0]
    : 'All'
  const hasActiveFilter = !!(labelFilter || assigneeFilter || phaseFilter || incompleteOnly || dueSoonOnly)
  const activeFilterCount = [labelFilter, assigneeFilter, phaseFilter, incompleteOnly, dueSoonOnly].filter(Boolean).length

  return (
    <KitToolbar
      className="tb-toolbar"
      style={{
        position: 'relative',
        zIndex: 5,
        flex: '0 0 auto',
        padding: '8px 22px',
        minHeight: 52,
        background: 'var(--color-panel-dark)',
        borderBottom: '1px solid var(--color-divider-dark)',
      }}
    >
      <ViewSwitcher
        view={view}
        onSetView={onSetView}
        views={[
          { key: 'list', label: 'List' },
          { key: 'board', label: 'Board' },
          { key: 'calendar', label: 'Calendar' },
          { key: 'table', label: 'Table' },
        ]}
      />

      <span className="cn-toolbar-divider" />

      <FilterBar count={activeFilterCount}>
        {/* Label filter */}
        <div className="cn-popover-wrap">
          <Chip as="button" active={!!labelFilter} onClick={() => toggle('label')}>
            <span style={{ color: 'var(--color-muted)' }}>Label</span> {labelFilterLabel}
            <CaretDown size={12} weight="bold" style={{ opacity: 0.6 }} />
          </Chip>
          <Popover open={open === 'label'} onClose={() => { setOpen(null); setNewLabelMode(false); setNewLabelName('') }}>
            <MenuItem
              key="all"
              active={(labelFilter || null) === null}
              onClick={() => {
                onSetLabelFilter(null)
                setOpen(null)
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 2, background: '#7A7568', flexShrink: 0 }} />
              All labels
            </MenuItem>
            {labels.map((l) => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center' }}>
                <MenuItem
                  active={(labelFilter || null) === l.id}
                  onClick={() => {
                    onSetLabelFilter(l.id)
                    setOpen(null)
                  }}
                  style={{ flex: 1 }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color, flexShrink: 0 }} />
                  {l.name}
                </MenuItem>
                {onUpdateLabel && onDeleteLabel && (
                  <LabelEditRow label={l} onUpdate={onUpdateLabel} onDelete={onDeleteLabel} />
                )}
              </div>
            ))}
            {onCreateLabel && (
              <div style={{ borderTop: '1px solid var(--color-divider-dark)', marginTop: 4, paddingTop: 4 }}>
                {newLabelMode ? (
                  <div style={{ padding: '4px 9px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input
                      value={newLabelName}
                      onChange={(e) => setNewLabelName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const t = newLabelName.trim()
                          if (t) { onCreateLabel({ name: t, color: newLabelColor }); setNewLabelName(''); setNewLabelMode(false) }
                        }
                        if (e.key === 'Escape') { setNewLabelMode(false); setNewLabelName('') }
                      }}
                      autoFocus
                      placeholder="Label name"
                      className="cn-input"
                      style={{ fontSize: 12, padding: '5px 7px' }}
                    />
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="color"
                        value={newLabelColor}
                        onChange={(e) => setNewLabelColor(e.target.value)}
                        style={{ width: 28, height: 28, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                      />
                      <Button
                        variant="gold"
                        size="sm"
                        onClick={() => {
                          const t = newLabelName.trim()
                          if (t) { onCreateLabel({ name: t, color: newLabelColor }); setNewLabelName(''); setNewLabelMode(false) }
                        }}
                        style={{ flex: 1, justifyContent: 'center' }}
                      >
                        Create
                      </Button>
                    </div>
                  </div>
                ) : (
                  <MenuItem onClick={() => setNewLabelMode(true)}>
                    ＋ New label
                  </MenuItem>
                )}
              </div>
            )}
          </Popover>
        </div>

        {/* Assignee filter */}
        <div className="cn-popover-wrap">
          <Chip as="button" active={!!assigneeFilter} onClick={() => toggle('assignee')}>
            <span style={{ color: 'var(--color-muted)' }}>Assignee</span> {assigneeFilterLabel}
            <CaretDown size={12} weight="bold" style={{ opacity: 0.6 }} />
          </Chip>
          <Popover open={open === 'assignee'} onClose={() => setOpen(null)}>
            <MenuItem
              active={!assigneeFilter}
              onClick={() => {
                onSetAssigneeFilter(null)
                setOpen(null)
              }}
            >
              All assignees
            </MenuItem>
            {users.map((u) => (
              <MenuItem
                key={u.uid}
                active={assigneeFilter === u.uid}
                onClick={() => {
                  onSetAssigneeFilter(u.uid)
                  setOpen(null)
                }}
              >
                <Avatar user={u} size={20} />
                {u.name}
              </MenuItem>
            ))}
          </Popover>
        </div>

        {/* Incomplete Only toggle */}
        <Chip as="button" active={incompleteOnly} onClick={() => onSetIncompleteOnly(!incompleteOnly)}>
          Incomplete
        </Chip>

        {/* Due Soon toggle */}
        <Chip as="button" active={dueSoonOnly} onClick={() => onSetDueSoonOnly(!dueSoonOnly)}>
          Due ≤14d
        </Chip>

        {/* Phase filter */}
        <Field
          as="select"
          value={phaseFilter || ''}
          onChange={(e) => onSetPhaseFilter(e.target.value || null)}
          style={{ fontSize: 12, padding: '6px 9px' }}
        >
          <option value="">All phases</option>
          <option value="p1">Phase 1 · 30</option>
          <option value="p2">Phase 2 · 60</option>
          <option value="p3">Phase 3 · 90</option>
        </Field>

        {hasActiveFilter && (
          <Button variant="subtle" size="sm" onClick={onClearFilters}>
            Clear
          </Button>
        )}
      </FilterBar>

      {/* Saved views — always mounted (not gated by Popover's open-unmount) so the
          view list and "Save view" affordance stay present for the SSR safety net;
          visibility is CSS-only, matching cn-popover's own look. */}
      <div ref={viewsRef} className="cn-popover-wrap">
        <Chip as="button" active={open === 'views'} onClick={() => toggle('views')}>
          Saved views
          <CaretDown size={12} weight="bold" style={{ opacity: 0.6 }} />
        </Chip>
        {open === 'views' && <div className="cn-popover-backdrop cn-popover-backdrop--sheet" onClick={() => setOpen(null)} />}
        <div className="cn-popover cn-popover--sheet" style={{ display: open === 'views' ? 'block' : 'none' }}>
          {(savedViews || []).length === 0 && (
            <div style={{ padding: '7px 9px', fontSize: 12, color: 'var(--color-muted)' }}>No saved views yet</div>
          )}
          {(savedViews || []).map((v) => (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center' }}>
              <MenuItem
                onClick={() => {
                  onApplySavedView(v)
                  setOpen(null)
                }}
                style={{ flex: 1 }}
              >
                {v.name}
              </MenuItem>
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteSavedView(v.id) }}
                title="Delete saved view"
                style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 12, padding: '2px 9px', lineHeight: 1 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-danger)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-muted)')}
              >
                ✕
              </button>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--color-divider-dark)', marginTop: 4, paddingTop: 4 }}>
            {newViewMode ? (
              <div style={{ padding: '4px 9px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const t = newViewName.trim()
                      if (t) { onSaveCurrentView(t); setNewViewName(''); setNewViewMode(false) }
                    }
                    if (e.key === 'Escape') { setNewViewMode(false); setNewViewName('') }
                  }}
                  autoFocus
                  placeholder="View name"
                  className="cn-input"
                  style={{ fontSize: 12, padding: '5px 7px' }}
                />
                <Button
                  variant="gold"
                  size="sm"
                  onClick={() => {
                    const t = newViewName.trim()
                    if (t) { onSaveCurrentView(t); setNewViewName(''); setNewViewMode(false) }
                  }}
                  style={{ justifyContent: 'center' }}
                >
                  Save view
                </Button>
              </div>
            ) : (
              <MenuItem onClick={() => setNewViewMode(true)}>
                ＋ Save view
              </MenuItem>
            )}
          </div>
        </div>
      </div>

      <ToolbarSpacer />

      {/* Add from Slack */}
      <div className="cn-popover-wrap">
        <Button variant="ghost" size="sm" onClick={() => toggle('slack')}>
          Add from Slack
        </Button>
        <Popover open={open === 'slack'} onClose={() => setOpen(null)} align="right">
          <div className="eyebrow" style={{ marginBottom: 12 }}>
            <span className="tag-label" style={{ color: 'var(--color-gold)' }}>Capture from Slack</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {SLACK_MESSAGES.map((msg) => (
              <div key={msg.id} style={{ border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '10px 11px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-muted)' }}>
                  <span style={{ color: 'var(--color-gold)' }}>{msg.channel}</span>·<span>{msg.author}</span>
                  <span style={{ marginLeft: 'auto' }}>{msg.time}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-white)', lineHeight: 1.4, marginBottom: 9 }}>{msg.text}</div>
                <button
                  onClick={() => {
                    onCreateFromSlack(msg)
                    setOpen(null)
                  }}
                  style={{ background: 'none', border: 'none', color: 'var(--color-gold)', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', padding: 0 }}
                >
                  + Create task
                </button>
              </div>
            ))}
          </div>
        </Popover>
      </div>
    </KitToolbar>
  )
}
